/**
 * T281 — Notification dropdown (bell icon in header).
 *
 * Polls every 10 s (via hook). Shows unread count badge; click opens a
 * panel with the last 10 notifications; each row navigates to `linkUrl`
 * and marks itself as read.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type NotificationDto,
} from '../../services/api/notifications';
import { SURFACE } from '../ui';
import { formatDateTimeBerlin } from '../../lib/format-date';

const TYPE_LABELS: Record<string, string> = {
  comment_added: 'Neuer Kommentar',
  nachträglicher_beleg: 'Beleg in geschlossenem Quartal',
  ki_budget_80: 'KI-Budget 80 %',
  ki_budget_100: 'KI-Budget 100 %',
  imap_failure: 'IMAP-Fehler',
};

export function NotificationDropdown() {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const navigate = useNavigate();

  const unread = (data?.items ?? []).filter((n) => !n.readAt).length;

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [open]);

  async function onClick(n: NotificationDto) {
    if (!n.readAt) {
      await markRead.mutateAsync(n.id);
    }
    if (n.linkUrl) {
      setOpen(false);
      navigate({ to: n.linkUrl });
    }
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-white/70 hover:text-white"
        aria-label="Benachrichtigungen"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] rounded-full bg-blue-400 text-zinc-900 font-medium">
            {unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            id={panelId}
            ref={panelRef}
            tabIndex={-1}
            role="region"
            aria-label="Benachrichtigungen"
            className={`fixed top-20 right-2 w-[min(24rem,calc(100vw-1rem))] max-h-[min(70vh,40rem)] overflow-y-auto rounded z-50 ${SURFACE.solid}`}
          >
            <div className="flex justify-between items-center p-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-white">Benachrichtigungen</h3>
              {unread > 0 && (
                <button
                  onClick={() => markAll.mutateAsync()}
                  className="text-xs text-white/50 hover:text-white"
                >
                  Alle als gelesen
                </button>
              )}
            </div>
            {(data?.items ?? []).length === 0 && (
              <p className="p-4 text-white/40 text-sm">Keine Benachrichtigungen.</p>
            )}
            <ul className="divide-y divide-white/5">
              {(data?.items ?? []).slice(0, 15).map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => onClick(n)}
                    className={`w-full text-left p-3 hover:bg-white/5 transition ${
                      n.readAt ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="text-sm text-white font-medium">
                      {TYPE_LABELS[n.type] ?? n.type}
                    </div>
                    <div className="text-xs text-white/50 mt-0.5">
                      {formatDateTimeBerlin(n.createdAt)}
                    </div>
                    {typeof n.payload.preview === 'string' && (
                      <div className="text-xs text-white/70 mt-1 line-clamp-2">
                        {String(n.payload.preview)}
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>, document.body
      )}
    </div>
  );
}
